import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChatInputComponent } from '../components/chat-input/chat-input.component';
import { ChatAttachedFile } from '../components/chat-input/chat-input.interface';
import { ChatPresetSelectComponent } from '../components/chat-preset-select/chat-preset-select.component';
import { ChatListItemComponent } from '../components/chats-list/chats-list-item/chats-list-item.component';
import { ChatsListComponent } from '../components/chats-list/chats-list.component';
import { ContainerComponent } from '../components/container/container.component';
import { ResizableSplitLayoutComponent } from '../components/resizable-split-layout/resizable-split-layout.component';
import { useRequest } from '../hooks/useRequest';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { refreshChats } from '../store/slices/chats-slice';
import { createChat, deleteChat, setChatPreset } from '../utils/axios/api';
import {
  formatMessageWithEntryContext,
  parseChatEntryContext,
} from '../utils/chat-entry-context/chat-entry-context';

export type PendingChatMessage = {
  text: string;
  files: ChatAttachedFile[];
  presetId: string | null;
};

export function ChatsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const dispatch = useAppDispatch();
  const chats = useAppSelector((state) => state.chats.items);
  const presets = useAppSelector((state) => state.presets.items);
  const sendRequest = useRequest();
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [attachedFiles, setAttachedFiles] = useState<ChatAttachedFile[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  const handleRemoveFile = useCallback((id: string) => {
    setAttachedFiles((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleDeleteChat = useCallback(
    async (chatId: string) => {
      await sendRequest.fetch(async () => {
        await deleteChat(chatId);
        await dispatch(refreshChats()).unwrap();
      });
    },
    [dispatch, sendRequest],
  );

  const handleSend = useCallback(
    async (text: string) => {
      abortRef.current?.abort();
      const abortController = new AbortController();
      abortRef.current = abortController;

      const files = attachedFiles;
      setAttachedFiles([]);

      await sendRequest.fetch(async () => {
        const chat = await createChat();

        if (abortController.signal.aborted) {
          return;
        }

        if (selectedPresetId) {
          await setChatPreset(chat.id, selectedPresetId);

          if (abortController.signal.aborted) {
            return;
          }
        }

        await dispatch(refreshChats()).unwrap();

        if (abortController.signal.aborted) {
          return;
        }

        navigate(`/chats/${chat.id}`, {
          replace: true,
          state: {
            text: formatMessageWithEntryContext(
              text,
              parseChatEntryContext(search),
            ),
            files,
            presetId: selectedPresetId,
          } satisfies PendingChatMessage,
        });
      });
    },
    [attachedFiles, dispatch, navigate, search, selectedPresetId, sendRequest],
  );

  return (
    <ResizableSplitLayoutComponent
      rightHeader={
        <ChatPresetSelectComponent
          presets={presets}
          value={selectedPresetId}
          onChange={setSelectedPresetId}
          disabled={sendRequest.isLoading}
        />
      }
      left={
        <ChatsListComponent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography variant="h5">{t('chats.title')}</Typography>
            <Tooltip title={t('chats.new')}>
              <IconButton
                size="small"
                aria-label={t('chats.new')}
                onClick={() => {
                  setSelectedPresetId(null);
                  setAttachedFiles([]);
                }}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {chats.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('chats.empty')}
            </Typography>
          ) : (
            chats.map((chat) => (
              <ChatListItemComponent
                key={chat.id}
                chat={chat}
                onClick={() => {
                  navigate(`/chats/${chat.id}`);
                }}
                onDelete={() => {
                  void handleDeleteChat(chat.id);
                }}
              />
            ))
          )}
        </ChatsListComponent>
      }
      right={
        <ContainerComponent>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              px: 2,
            }}
          >
            <Box sx={{ width: '100%' }}>
              {attachedFiles.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.75,
                    mb: 1,
                    px: 0.5,
                  }}
                >
                  {attachedFiles.map(({ id, file }) => (
                    <Chip
                      key={id}
                      label={file.name}
                      size="small"
                      onDelete={
                        sendRequest.isLoading
                          ? undefined
                          : () => handleRemoveFile(id)
                      }
                      aria-label={t('chatInput.removeFile', {
                        name: file.name,
                      })}
                    />
                  ))}
                </Box>
              ) : null}
              <ChatInputComponent
                value={attachedFiles}
                onChange={setAttachedFiles}
                onSend={(text) => {
                  void handleSend(text);
                }}
                isStreaming={sendRequest.isLoading}
              />
            </Box>
          </Box>
        </ContainerComponent>
      }
    />
  );
}
